import { ImageLightboxController } from 'Components/controllers/image-lightbox-controller';
import { html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import Styles from './vm-image-thumbnail.scss';

/**
 * Generic clickable image thumbnail that opens the full-resolution image in a lightbox.
 *
 * Renders a square, `object-fit: cover` preview inside a native button, so click, Enter
 * and Space all open the image. The thumbnail owns its `<vm-image-lightbox>` through
 * `ImageLightboxController`: the lightbox module is loaded on the first open (so pages
 * that never open an image don't pay for it) and rendered next to the button while open.
 * Its native `<dialog>` sits in the top layer, so ancestor overflow / transforms don't
 * clip it.
 *
 * Light-DOM children are never rendered (there is no `<slot>`), so server-rendered markup
 * can put a plain `<img>` inside as a fallback shown only until the element upgrades.
 *
 * @attr src       - Full-resolution image URL, shown in the lightbox. Also used for the
 *                   inline image if `thumb-src` is omitted. Nothing opens while it's empty.
 * @attr alt       - Alt text describing the image (for screen readers).
 * @attr thumb-src - Optional smaller thumbnail URL; the lightbox still shows `src`.
 *
 * @cssprop [--vm-image-thumbnail-size=75px] - Edge length of the square thumbnail.
 * @cssprop [--vm-image-thumbnail-radius=4px] - Corner radius.
 *
 * @csspart button - The clickable wrapper around the image.
 * @csspart image  - The thumbnail `<img>` element.
 *
 * @example
 * ```html
 * <vm-image-thumbnail
 *   src="/media/photo-600.jpg"
 *   thumb-src="/media/photo-75.jpg"
 *   alt="X-ray of left knee, AP view"
 * ></vm-image-thumbnail>
 * ```
 */
@customElement('vm-image-thumbnail')
export class VmImageThumbnail extends LitElement {
    static override styles = Styles;

    /** Full-resolution image URL — shown in the lightbox. */
    @property()
    src = '';

    /** Alt text. */
    @property()
    alt = '';

    /** Optional smaller thumbnail URL; if omitted, `src` is used for the inline image too. */
    @property({ attribute: 'thumb-src' })
    thumbSrc = '';

    private _lightbox = new ImageLightboxController(this);

    private _onClick = () => {
        // An empty src (e.g. a thumbnail that failed to generate) is ignored by the controller.
        this._lightbox.open(this.src, this.alt);
    };

    override render() {
        return html`
            <button
                type="button"
                class="vm-image-thumbnail"
                part="button"
                aria-label=${this.alt || 'Open image'}
                @click=${this._onClick}
            >
                <img
                    class="vm-image-thumbnail__image"
                    part="image"
                    src=${this.thumbSrc || this.src}
                    alt=${this.alt}
                    loading="lazy"
                    decoding="async"
                />
            </button>
            ${this._lightbox.render()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'vm-image-thumbnail': VmImageThumbnail;
    }
}
